import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { FadeIn } from '../components/ui/FadeIn';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
// EmailJS configuration
const EMAILJS_PUBLIC_KEY = 'tbiu65TyTB-bBxgpd';
const EMAILJS_SERVICE_ID = 'service_92ey6mj';
// Internal notification template — temporarily disabled while testing the client auto-reply only
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EMAILJS_INTERNAL_TEMPLATE_ID = 'template_37i6iar';
const EMAILJS_AUTOREPLY_TEMPLATE_ID = 'template_j89wpko';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays, isBefore, startOfToday } from 'date-fns';
import { ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Calendar State
  const [currentMonth, setCurrentMonth] = useState(startOfToday());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const timeSlots = ['9:00 AM', '10:30 AM', '1:00 PM', '2:30 PM', '4:00 PM'];
  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    location: '',
    venue: '',
    partySize: '',
    details: ''
  });
  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      service: '',
      location: '',
      venue: '',
      partySize: '',
      details: ''
    });
    setSelectedDate(null);
    setSelectedTime(null);
    setCurrentMonth(startOfToday());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.email) newErrors.email = 'Email is required';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!selectedDate) newErrors.date = 'Please select a date';
    if (!selectedTime) newErrors.time = 'Please select a time';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent duplicate submissions while processing
    if (isSubmitting) return;
    if (!validateForm()) return;
    setSubmitError('');
    setIsSubmitting(true);
    // Template variables must match the EmailJS template exactly
    const templateParams = {
      client_name: formData.fullName,
      client_email: formData.email,
      client_phone: formData.phone,
      event_date: selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : '',
      event_time: selectedTime || '',
      getting_ready_location: formData.location,
      venue_name: formData.venue,
      services_requested: formData.service,
      bridal_party_size: formData.partySize,
      additional_notes: formData.details
    };
    try {
      // TEST MODE: send ONLY the client auto-reply email using the auto-reply template.
      // The internal notification is intentionally disabled for this test.
      // The auto-reply template is configured to send to {{client_email}}.
      const autoReplyResponse = await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_AUTOREPLY_TEMPLATE_ID, templateParams, {
        publicKey: EMAILJS_PUBLIC_KEY
      });
      console.log('EmailJS client auto-reply sent:', autoReplyResponse.status, autoReplyResponse.text);
      // Email sent successfully — clear the form and show success
      resetForm();
      setIsSuccess(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (err) {
      // If either email fails, log the exact error and show the error message
      console.error('EmailJS send failed:', err);
      setSubmitError('Something went wrong while sending your inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Calendar Logic
  const renderHeader = () => {
    return <div className="flex justify-between items-center mb-6" data-id="element-193">
        <button type="button" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-champagne/20 rounded-full transition-colors" data-id="element-194">
          <ChevronLeft className="w-5 h-5 text-espresso" data-id="element-195" />
        </button>
        <h3 className="text-lg font-serif text-espresso" data-id="element-196">
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button type="button" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-champagne/20 rounded-full transition-colors" data-id="element-197">
          <ChevronRight className="w-5 h-5 text-espresso" data-id="element-198" />
        </button>
      </div>;
  };
  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentMonth);
    for (let i = 0; i < 7; i++) {
      days.push(<div key={i} className="text-center text-xs font-medium text-taupe uppercase tracking-wider py-2" data-id="element-199">
          {format(addDays(startDate, i), 'EEEEEE')}
        </div>);
    }
    return <div className="grid grid-cols-7 mb-2" data-id="element-200">{days}</div>;
  };
  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = '';
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, 'd');
        const cloneDay = day;
        const isPast = isBefore(day, startOfToday());
        const isSelected = selectedDate && isSameDay(day, selectedDate);
        const isCurrentMonth = isSameMonth(day, monthStart);
        days.push(<div key={day.toString()} className={`p-1 flex justify-center items-center h-12 ${!isCurrentMonth ? 'opacity-30' : ''}`} data-id="element-201">
            <button type="button" disabled={isPast} onClick={() => {
            setSelectedDate(cloneDay);
            if (errors.date) setErrors(prev => ({
              ...prev,
              date: ''
            }));
          }} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all
                ${isSelected ? 'bg-espresso text-ivory' : ''}
                ${!isSelected && !isPast && isCurrentMonth ? 'hover:bg-champagne/30 text-espresso' : ''}
                ${isPast ? 'text-taupe/40 cursor-not-allowed' : ''}
              `} data-id="element-202">
              {formattedDate}
            </button>
          </div>);
        day = addDays(day, 1);
      }
      rows.push(<div className="grid grid-cols-7" key={day.toString()} data-id="element-203">
          {days}
        </div>);
      days = [];
    }
    return <div data-id="element-204">{rows}</div>;
  };
  return <div className="bg-ivory" data-id="element-205">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-206">
        <div className="absolute inset-0 z-0" data-id="element-207">
          <img src="https://cdn.magicpatterns.com/uploads/wGjMmJ92iZDgchyXes8TNf/6a.png" alt="Woman booking a beauty appointment in a calm, elegant space" className="w-full h-full object-cover object-center" data-id="element-208" />
          <div className="absolute inset-0 bg-espresso/50 mix-blend-multiply" data-id="element-209" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-210">
          <FadeIn delay={0.2} data-id="element-211">
            <h1 className="text-5xl md:text-7xl text-ivory mb-6 text-balance" data-id="element-212">
              Inquire to Book
            </h1>
          </FadeIn>
          <FadeIn delay={0.4} data-id="element-213">
            <p className="text-lg md:text-xl text-cream font-light text-balance" data-id="element-214">
              We would be honored to bring the Aura experience to you.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 px-6" data-id="element-215">
        <div className="max-w-4xl mx-auto" data-id="element-216">
          {isSuccess ? <FadeIn className="bg-white p-12 md:p-20 text-center border border-champagne/50" data-id="element-217">
              <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-6" data-id="element-218" />
              <h2 className="text-3xl md:text-4xl font-serif text-espresso mb-4" data-id="element-219">
                Inquiry Received
              </h2>
              <p className="text-taupe text-lg font-light leading-relaxed max-w-lg mx-auto mb-8" data-id="element-220">
                Thank you for your inquiry. We've received your details and will
                be in touch soon.
              </p>
              <Button onClick={() => setIsSuccess(false)} variant="outline" data-id="element-221">
                Submit Another Inquiry
              </Button>
            </FadeIn> : <FadeIn data-id="element-222">
              <div className="text-center mb-16" data-id="element-223">
                <p className="text-lg text-taupe font-light leading-relaxed max-w-2xl mx-auto" data-id="element-224">
                  For bridal bookings and signature beauty appointments, please
                  submit your inquiry below. We look forward to creating a
                  beauty experience that feels seamless, elevated, and
                  beautifully personal.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 border border-champagne/50 shadow-sm" data-id="element-225">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-226">
                  {/* Personal Details */}
                  <div className="space-y-6" data-id="element-227">
                    <h3 className="text-xl font-serif text-espresso border-b border-champagne/50 pb-2 mb-6" data-id="element-228">
                      Your Details
                    </h3>

                    <div data-id="element-229">
                      <label className="block text-sm text-taupe mb-2" data-id="element-230">
                        Full Name *
                      </label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} className={`w-full bg-ivory border ${errors.fullName ? 'border-red-400' : 'border-champagne'} px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors`} data-id="element-231" />
                      {errors.fullName && <p className="text-red-500 text-xs mt-1" data-id="element-232">
                          {errors.fullName}
                        </p>}
                    </div>

                    <div data-id="element-233">
                      <label className="block text-sm text-taupe mb-2" data-id="element-234">
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full bg-ivory border ${errors.email ? 'border-red-400' : 'border-champagne'} px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors`} data-id="element-235" />
                      {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-236">
                          {errors.email}
                        </p>}
                    </div>

                    <div data-id="element-237">
                      <label className="block text-sm text-taupe mb-2" data-id="element-238">
                        Phone Number *
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={`w-full bg-ivory border ${errors.phone ? 'border-red-400' : 'border-champagne'} px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors`} data-id="element-239" />
                      {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-240">
                          {errors.phone}
                        </p>}
                    </div>

                    <div data-id="element-241">
                      <label className="block text-sm text-taupe mb-2" data-id="element-242">
                        Service of Interest *
                      </label>
                      <select name="service" value={formData.service} onChange={handleInputChange} className={`w-full bg-ivory border ${errors.service ? 'border-red-400' : 'border-champagne'} px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors appearance-none`} data-id="element-243">
                        <option value="" data-id="element-244">Select a service...</option>
                        <option value="Bridal - Signature" data-id="element-245">
                          The Signature Bride Experience
                        </option>
                        <option value="Bridal - Glow" data-id="element-246">
                          The Luxury Bridal Glow Experience
                        </option>
                        <option value="Bridal - VIP" data-id="element-247">
                          The Ultimate VIP Bride Experience
                        </option>
                        <option value="Beauty - Brow" data-id="element-248">Brow Design</option>
                        <option value="Beauty - Lash" data-id="element-249">Korean Lash Lift</option>
                        <option value="Beauty - Facial" data-id="element-250">
                          Custom Glow Facial
                        </option>
                        <option value="Beauty - Social" data-id="element-251">Social Makeup</option>
                        <option value="Beauty - PMU" data-id="element-252">PMU Brow</option>
                      </select>
                      {errors.service && <p className="text-red-500 text-xs mt-1" data-id="element-253">
                          {errors.service}
                        </p>}
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="space-y-6" data-id="element-254">
                    <h3 className="text-xl font-serif text-espresso border-b border-champagne/50 pb-2 mb-6" data-id="element-255">
                      Event Details
                    </h3>

                    <div data-id="element-256">
                      <label className="block text-sm text-taupe mb-2" data-id="element-257">
                        Getting Ready Location (City or Home) *
                      </label>
                      <input type="text" name="location" value={formData.location} onChange={handleInputChange} className="w-full bg-ivory border border-champagne px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors" data-id="element-258" />
                    </div>

                    <div data-id="element-259">
                      <label className="block text-sm text-taupe mb-2" data-id="element-260">
                        Venue Name (if applicable)
                      </label>
                      <input type="text" name="venue" value={formData.venue} onChange={handleInputChange} className="w-full bg-ivory border border-champagne px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors" data-id="element-261" />
                    </div>

                    <div data-id="element-262">
                      <label className="block text-sm text-taupe mb-2" data-id="element-263">
                        Bridal Party Size (if applicable)
                      </label>
                      <input type="text" name="partySize" value={formData.partySize} onChange={handleInputChange} placeholder="e.g., Bride + 4 Bridesmaids" className="w-full bg-ivory border border-champagne px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors" data-id="element-264" />
                    </div>

                    <div data-id="element-265">
                      <label className="block text-sm text-taupe mb-2" data-id="element-266">
                        Additional Details
                      </label>
                      <textarea name="details" value={formData.details} onChange={handleInputChange} rows={4} className="w-full bg-ivory border border-champagne px-4 py-3 text-espresso focus:outline-none focus:border-gold transition-colors resize-none" placeholder="Tell us a bit about your vision..." data-id="element-267" />
                    </div>
                  </div>
                </div>

                {/* Calendar & Time Selection */}
                <div className="border-t border-champagne/50 pt-10 mb-12" data-id="element-268">
                  <h3 className="text-xl font-serif text-espresso mb-8 text-center" data-id="element-269">
                    Select Date & Time
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-270">
                    {/* Calendar */}
                    <div className="bg-ivory p-6 border border-champagne/30" data-id="element-271">
                      {renderHeader()}
                      {renderDays()}
                      {renderCells()}
                      {errors.date && <p className="text-red-500 text-xs mt-2 text-center" data-id="element-272">
                          {errors.date}
                        </p>}
                    </div>

                    {/* Time Slots */}
                    <div data-id="element-273">
                      <h4 className="text-sm text-taupe mb-4 uppercase tracking-wider" data-id="element-274">
                        {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Select a date first'}
                      </h4>

                      <div className="grid grid-cols-2 gap-3" data-id="element-275">
                        {timeSlots.map(time => <button key={time} type="button" disabled={!selectedDate} onClick={() => {
                      setSelectedTime(time);
                      if (errors.time) setErrors(prev => ({
                        ...prev,
                        time: ''
                      }));
                    }} className={`py-3 px-4 border text-sm transition-all
                              ${!selectedDate ? 'opacity-50 cursor-not-allowed border-champagne/50 text-taupe' : ''}
                              ${selectedDate && selectedTime === time ? 'bg-espresso border-espresso text-ivory' : ''}
                              ${selectedDate && selectedTime !== time ? 'border-champagne text-espresso hover:border-gold' : ''}
                            `} data-id="element-276">
                            {time}
                          </button>)}
                      </div>
                      {errors.time && <p className="text-red-500 text-xs mt-2" data-id="element-277">
                          {errors.time}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="text-center" data-id="element-278">
                  {submitError && <p className="text-red-500 text-sm mb-4" data-id="element-279">{submitError}</p>}
                  <Button type="submit" variant="primary" className="w-full md:w-auto min-w-[200px] disabled:opacity-60 disabled:cursor-not-allowed" disabled={isSubmitting} data-id="element-280">
                    {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                  </Button>
                </div>
              </form>
            </FadeIn>}
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-24 bg-cream text-center px-6" data-id="element-281">
        <FadeIn data-id="element-282">
          <h2 className="text-3xl md:text-4xl font-serif text-espresso italic" data-id="element-283">
            Refined beauty. Intentional care. Luxury brought to you.
          </h2>
        </FadeIn>
      </section>
    </div>;
}